import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  collectionGroup,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { useAuth } from "../../../../context/AuthContext";

// ======================================================
// ✅ Firestore names for Sensor Stock (change here only)
// ======================================================
const SLEEPSETS_COLLECTION = "sleepSets"; // sleepSets/{sleepSetDocId}
const SENSOR_SUBCOLLECTION = "sleepstudysensors"; // sleepSets/{sleepSetDocId}/sleepstudysensors/{sensorId}
const SENSOR_TRANSFERS_COLLECTION = "sensorTransfers"; // log collection
const SENSOR_REQUESTS_COLLECTION = "sensorRequests"; // requests collection
// ======================================================

const itemsPerPage = 3;

const SensorStockPage = () => {
  const { currentUser } = useAuth();

  const [transactionPageMap, setTransactionPageMap] = useState({});
  const [requestPageMap, setRequestPageMap] = useState({});

  const [sleepSets, setSleepSets] = useState([]); // ✅ locations A-Z + BrainDynamics
  const [sensorBySet, setSensorBySet] = useState({}); // ✅ grouped by setDocId
  const [transfers, setTransfers] = useState([]);
  const [requests, setRequests] = useState([]);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddCountModal, setShowAddCountModal] = useState(false);
  const [showReleaseConsumeModal, setShowReleaseConsumeModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const [transferData, setTransferData] = useState({
    fromSetDocId: "",
    toSetDocId: "",
    sensorId: "",
    quantity: 1,
    note: "",
    name: "",
    currentQuantity: 0,
    itemType: "count",
    serialNumber: "",
  });

  const [addCountData, setAddCountData] = useState({
    setDocId: "",
    sensorId: "",
    currentQuantity: 0,
    name: "",
    quantityToAdd: 1,
  });

  const [newSensor, setNewSensor] = useState({
    setDocId: "",
    name: "",
    type: "count",
    quantity: 1,
    serialNumber: "",
    note: "",
  });

  const [requestData, setRequestData] = useState({
    setDocId: "",
    name: "",
    itemType: "count",
    quantity: 1,
    note: "",
  });

  const [releaseConsumeData, setReleaseConsumeData] = useState({
    setDocId: "",
    sensorId: "",
    currentQuantity: 0,
    name: "",
    quantity: 1,
    type: "scrap",
    itemType: "count",
    serialNumber: "",
    note: "",
  });

  // ---------- pagination helpers ----------
  const getSetTxPage = (setDocId) => transactionPageMap[setDocId] || 1;
  const updateSetTxPage = (setDocId, page) => {
    setTransactionPageMap((prev) => ({ ...prev, [setDocId]: page }));
  };
  const getSetReqPage = (setDocId) => requestPageMap[setDocId] || 1;
  const updateSetReqPage = (setDocId, page) => {
    setRequestPageMap((prev) => ({ ...prev, [setDocId]: page }));
  };

  // ---------- set sort: BrainDynamics first, then A-Z ----------
  const sortedSets = useMemo(() => {
    const key = (s) =>
      String(s.setId || s.id || "")
        .trim()
        .toUpperCase();

    return [...sleepSets].sort((a, b) => {
      const A = key(a);
      const B = key(b);

      const aIsBDT = A === "BDT";
      const bIsBDT = B === "BDT";

      if (aIsBDT && !bIsBDT) return -1;
      if (!aIsBDT && bIsBDT) return 1;

      return A.localeCompare(B, undefined, { sensitivity: "base" });
    });
  }, [sleepSets]);

  // ---------- load data ----------
  const fetchData = async () => {
    // ✅ sleepSets
    const setSnap = await getDocs(collection(db, SLEEPSETS_COLLECTION));
    const setList = setSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setList.sort((a, b) => {
      // ✅ BDT first
      if (a.id === "BDT") return -1;
      if (b.id === "BDT") return 1;

      // then sort by setId (fallback to id) A-Z
      const A = String(a.setId || a.id);
      const B = String(b.setId || b.id);
      return A.localeCompare(B, undefined, { sensitivity: "base" });
    });

    setSleepSets(setList);

    // ✅ sensors (under sleepSets/*/sleepstudysensors)
    const sensorSnap = await getDocs(collectionGroup(db, SENSOR_SUBCOLLECTION));

    const grouped = {};
    sensorSnap.docs.forEach((docSnap) => {
      const sensor = docSnap.data();

      // path: sleepSets/{setDocId}/sleepstudysensors/{sensorId}
      const seg = docSnap.ref.path.split("/");
      const setIdx = seg.indexOf(SLEEPSETS_COLLECTION);
      const setDocId = seg[setIdx + 1];

      if (!grouped[setDocId]) grouped[setDocId] = [];
      grouped[setDocId].push({ id: docSnap.id, ...sensor });
    });

    Object.keys(grouped).forEach((setDocId) => {
      grouped[setDocId].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
    });

    setSensorBySet(grouped);

    // ✅ transfers log
    const transfersSnap = await getDocs(
      collection(db, SENSOR_TRANSFERS_COLLECTION),
    );
    const transferList = transfersSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    setTransfers(
      transferList.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      ),
    );

    // ✅ requests
    const reqSnap = await getDocs(collection(db, SENSOR_REQUESTS_COLLECTION));
    const reqList = reqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setRequests(
      reqList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    );
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showTransferModal,
    showCreateModal,
    showAddCountModal,
    showReleaseConsumeModal,
    showRequestModal,
  ]);

  // ---------- auto fulfill (when stock arrives to a set) ----------
  const autoFulfillRequests = async ({
    setDocId,
    name,
    itemType,
    addedQuantity,
  }) => {
    if (!setDocId || !name || !itemType || !addedQuantity) return;

    const reqSnap = await getDocs(collection(db, SENSOR_REQUESTS_COLLECTION));
    const open = reqSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(
        (r) =>
          r.setDocId === setDocId &&
          String(r.name || "").trim() === String(name || "").trim() &&
          r.itemType === itemType &&
          r.status === "open",
      )
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    let remaining = addedQuantity;

    for (const r of open) {
      if (remaining <= 0) break;

      const already = Number(r.fulfilledQuantity || 0);
      const need = Math.max(0, Number(r.requestedQuantity || 0) - already);
      if (need <= 0) continue;

      const fulfill = itemType === "serial" ? 1 : Math.min(need, remaining);

      await updateDoc(doc(db, SENSOR_REQUESTS_COLLECTION, r.id), {
        fulfilledQuantity: already + fulfill,
        status:
          already + fulfill >= Number(r.requestedQuantity || 0)
            ? "fulfilled"
            : "open",
      });

      remaining -= itemType === "serial" ? 1 : fulfill;
    }
  };

  // ---------- UI render sensor item (2-row layout) ----------
  const renderSensor = (item, setDocId) => {
    const isSerial = item.type === "serial";
    const rightText = isSerial
      ? `SN: ${item.serialNumber || "-"}`
      : `Stock: ${Number(item.quantity || 0)}`;

    return (
      <div className="w-100">
        {/* Row 1 */}
        <div className="d-flex justify-content-between align-items-start">
          <div className="pr-2" style={{ minWidth: 0 }}>
            <div className="font-weight-bold text-truncate">
              {" "}
              {String(item.name || "").trim()}
            </div>
            {item.note ? (
              <div className="small text-muted text-truncate">{item.note}</div>
            ) : null}
          </div>

          <div className="text-right" style={{ whiteSpace: "nowrap" }}>
            <span
              className={`badge ${isSerial ? "badge-info" : "badge-success"} mr-2`}
            >
              {isSerial ? "Serial" : "Count"}
            </span>
            <span className="text-muted small">{rightText}</span>
          </div>
        </div>

        {/* Row 2 */}
        <div className="d-flex flex-wrap mt-2" style={{ gap: 6 }}>
          <button
            className="btn btn-sm btn-outline-primary py-0 px-2"
            onClick={() => {
              setTransferData({
                fromSetDocId: setDocId,
                toSetDocId: "",
                sensorId: item.id,
                quantity: 1,
                note: "",
                name: item.name,
                currentQuantity: item.quantity || 0,
                itemType: item.type,
                serialNumber: item.serialNumber || "",
              });
              setShowTransferModal(true);
            }}
          >
            Transfer to Set
          </button>

          {item.type === "count" && (
            <button
              className="btn btn-sm btn-outline-success py-0 px-3"
              onClick={() => {
                setAddCountData({
                  setDocId,
                  sensorId: item.id,
                  currentQuantity: item.quantity || 0,
                  name: item.name,
                  quantityToAdd: 1,
                });
                setShowAddCountModal(true);
              }}
            >
              Add
            </button>
          )}

          <button
            className="btn btn-sm btn-outline-warning py-0 px-2"
            onClick={() => {
              setReleaseConsumeData({
                setDocId,
                sensorId: item.id,
                currentQuantity: item.quantity || 1,
                name: item.name,
                quantity: 1,
                type: "scrap",
                itemType: item.type,
                serialNumber: item.serialNumber || "",
                note: "",
              });
              setShowReleaseConsumeModal(true);
            }}
          >
            Scrap / Consume
          </button>

          <button
            className="btn btn-sm btn-outline-danger py-0 px-2"
            onClick={async () => {
              const ok = window.confirm(`Delete "${item.name}"?`);
              if (!ok) return;

              const itemRef = doc(
                db,
                `${SLEEPSETS_COLLECTION}/${setDocId}/${SENSOR_SUBCOLLECTION}/${item.id}`,
              );
              await deleteDoc(itemRef);
              await fetchData();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ---------- actions ----------
  const handleTransfer = async () => {
    const { fromSetDocId, toSetDocId, sensorId, quantity, note } = transferData;
    if (!fromSetDocId || !toSetDocId || !sensorId) return;
    if (fromSetDocId === toSetDocId) return;

    const fromRef = doc(
      db,
      `${SLEEPSETS_COLLECTION}/${fromSetDocId}/${SENSOR_SUBCOLLECTION}/${sensorId}`,
    );
    const fromSnap = await getDoc(fromRef);
    if (!fromSnap.exists()) return;

    const sensor = fromSnap.data();
    const isSerial = sensor.type === "serial";

    if (isSerial) {
      const toRef = doc(
        db,
        `${SLEEPSETS_COLLECTION}/${toSetDocId}/${SENSOR_SUBCOLLECTION}/${sensorId}`,
      );
      await setDoc(toRef, sensor);
      await deleteDoc(fromRef);
    } else {
      const qty = Number(quantity || 0);
      const current = Number(sensor.quantity || 0);
      if (qty <= 0) return;
      if (qty > current) {
        alert(`Not enough stock. Current stock: ${current}`);
        return;
      }

      // decrease source
      await updateDoc(fromRef, { quantity: Math.max(0, current - qty) });

      // add to destination (merge by same name + count)
      const toCol = collection(
        db,
        `${SLEEPSETS_COLLECTION}/${toSetDocId}/${SENSOR_SUBCOLLECTION}`,
      );
      const toSnap = await getDocs(toCol);

      const existing = toSnap.docs.find(
        (d) => d.data().name === sensor.name && d.data().type === "count",
      );

      if (existing) {
        await updateDoc(existing.ref, {
          quantity: Number(existing.data().quantity || 0) + qty,
        });
      } else {
        await addDoc(toCol, {
          ...sensor,
          quantity: qty,
          timestamp: new Date().toISOString(),
          createdBy: currentUser.displayName || "",
        });
      }
    }

    // log transfer
    const fromSet = sleepSets.find((s) => s.id === fromSetDocId);
    const toSet = sleepSets.find((s) => s.id === toSetDocId);

    await addDoc(collection(db, SENSOR_TRANSFERS_COLLECTION), {
      type: "SET_TO_SET",
      sensorId,
      name: sensor.name || "",
      itemType: sensor.type || "",
      serialNumber: sensor.serialNumber || "",
      quantity: isSerial ? 1 : Number(quantity || 0),
      fromSetDocId,
      fromSetId: fromSet.setId || fromSet.id || "",
      toSetDocId,
      toSetId: toSet.setId || toSet.id || "",
      note: note || "",
      timestamp: new Date().toISOString(),
      createdBy: currentUser.displayName || "",
    });

    // auto-fulfill requests in destination
    await autoFulfillRequests({
      setDocId: toSetDocId,
      name: sensor.name,
      itemType: sensor.type,
      addedQuantity: isSerial ? 1 : Number(quantity || 0),
    });

    setShowTransferModal(false);
    setTransferData({
      fromSetDocId: "",
      toSetDocId: "",
      sensorId: "",
      quantity: 1,
      note: "",
      name: "",
      currentQuantity: 0,
      itemType: "count",
      serialNumber: "",
    });

    await fetchData();
  };

  const handleCreateSensor = async () => {
    const { setDocId, name, type, quantity, serialNumber, note } = newSensor;

    const cleanName = String(name || "").trim(); // ✅ trim
    const cleanNote = String(note || "").trim(); // (optional)
    const cleanSerial = String(serialNumber || "").trim(); // (optional)

    if (!setDocId || !cleanName || (type === "serial" && !cleanSerial)) return;

    const colRef = collection(
      db,
      `${SLEEPSETS_COLLECTION}/${setDocId}/${SENSOR_SUBCOLLECTION}`,
    );
    const snap = await getDocs(colRef);

    let createdId = null;
    let addedQtyForRequests = 0;

    if (type === "count") {
      const existing = snap.docs.find(
        (d) =>
          String(d.data().name || "").trim() === cleanName &&
          d.data().type === "count",
      );

      if (existing) {
        await updateDoc(existing.ref, {
          quantity:
            Number(existing.data().quantity || 0) + Number(quantity || 0),
        });
        createdId = existing.id;
        addedQtyForRequests = Number(quantity || 0);
      } else {
        const docRef = await addDoc(colRef, {
          name: cleanName, // ✅ ใช้ชื่อที่ trim แล้ว
          type,
          quantity: Number(quantity || 0),
          serialNumber: "",
          note: cleanNote,
          timestamp: new Date().toISOString(),
          createdBy: currentUser.displayName || "",
        });
        createdId = docRef.id;
        addedQtyForRequests = Number(quantity || 0);
      }
    } else {
      const dup = snap.docs.find(
        (d) => String(d.data().serialNumber || "").trim() === cleanSerial,
      );
      if (dup) {
        alert("This serial number already exists in this Sleep Set.");
        return;
      }

      const docRef = await addDoc(colRef, {
        name: cleanName, // ✅ trim
        type,
        quantity: 1,
        serialNumber: cleanSerial, // ✅ trim
        note: cleanNote, // ✅ trim (optional)
        timestamp: new Date().toISOString(),
        createdBy: currentUser.displayName || "",
      });
      createdId = docRef.id;
      addedQtyForRequests = 1;
    }

    // ...ส่วนล่างใช้ cleanName ด้วย (log / autoFulfill) จะดีมาก
  };

  const handleCreateRequest = async () => {
    const { setDocId, name, itemType, quantity, note } = requestData;

    const cleanName = String(name || "").trim();
    const cleanNote = String(note || "").trim();

    if (!setDocId || !cleanName || !itemType) return;

    const qty = itemType === "serial" ? 1 : Number(quantity) || 1;

    await addDoc(collection(db, SENSOR_REQUESTS_COLLECTION), {
      setDocId,
      name: cleanName, // ✅
      itemType,
      requestedQuantity: qty,
      fulfilledQuantity: 0,
      status: "open",
      note: cleanNote, // ✅ optional
      timestamp: new Date().toISOString(),
      createdBy: currentUser.displayName || "",
    });

    setShowRequestModal(false);
    await fetchData();
  };

  const cancelRequest = async (reqId) => {
    if (!window.confirm("Cancel this request?")) return;
    await updateDoc(doc(db, SENSOR_REQUESTS_COLLECTION, reqId), {
      status: "cancelled",
    });
    await fetchData();
  };

  // ======================================================
  // ✅ UI
  // ======================================================
  return (
    <div className="container-fluid mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="mb-2 mb-md-0">
          <h5 className="mb-0">Sensor Stock (Sleep Sets)</h5>
          <small className="text-muted">
            Track sensors per Sleep Set (A–Z, BrainDynamics), create requests,
            and transfer between sets.
          </small>
        </div>

        <button
          className="btn btn-success"
          onClick={() => setShowCreateModal(true)}
        >
          + Add Sensor
        </button>
      </div>

      {sortedSets.map((set) => {
        const setDocId = set.id;
        const setId = set.setId || set.id;

        const setTransfers = transfers.filter(
          (t) => t.fromSetDocId === setDocId || t.toSetDocId === setDocId,
        );

        const setRequestsOpen = requests.filter(
          (r) => r.setDocId === setDocId && r.status === "open",
        );

        const currentTxPage = getSetTxPage(setDocId);
        const totalTxPages = Math.max(
          1,
          Math.ceil(setTransfers.length / itemsPerPage),
        );
        const paginatedTransfers = setTransfers.slice(
          (currentTxPage - 1) * itemsPerPage,
          currentTxPage * itemsPerPage,
        );

        const currentReqPage = getSetReqPage(setDocId);
        const totalReqPages = Math.max(
          1,
          Math.ceil(setRequestsOpen.length / itemsPerPage),
        );
        const paginatedRequests = setRequestsOpen.slice(
          (currentReqPage - 1) * itemsPerPage,
          currentReqPage * itemsPerPage,
        );

        const latestTxDateStr = (() => {
          if (setTransfers.length === 0) return "—";
          const latest = setTransfers.reduce((max, t) => {
            const tTime = Date.parse(t.timestamp || "");
            const mTime = Date.parse(max.timestamp || "");
            return isNaN(mTime) || (!isNaN(tTime) && tTime > mTime) ? t : max;
          });
          const d = new Date(latest.timestamp);
          return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB");
        })();

        const allSensors = (sensorBySet[setDocId] || []).slice();
        const serialItems = allSensors.filter((x) => x.type === "serial");
        const countItems = allSensors.filter((x) => x.type === "count");

        return (
          <div key={setDocId} className="mb-4">
            <div className="card shadow-sm border-0">
              {/* ✅ header color for sensors */}
              <div
                className="card-header text-white d-flex flex-wrap justify-content-between align-items-center"
                style={{ backgroundColor: "#0EA5A5" }}
              >
                <div className="d-flex flex-column">
                  <strong style={{ fontSize: 16 }}>{setId}</strong>
                  <small className="text-light">
                    Last transaction: {latestTxDateStr}{" "}
                    {set.currentLocation
                      ? `· Current: ${set.currentLocation}`
                      : ""}
                  </small>
                </div>

                <div className="mt-2 mt-md-0">
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      setRequestData({
                        setDocId,
                        name: "",
                        itemType: "count",
                        quantity: 1,
                        note: "",
                      });
                      setShowRequestModal(true);
                    }}
                  >
                    + New Request
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="row">
                  {/* LEFT: STOCK */}
                  <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Stock</h6>
                      <small className="text-muted">
                        {allSensors.length} items
                      </small>
                    </div>

                    <div
                      className="border rounded"
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        style={{
                          maxHeight: "70vh",
                          overflowY: "auto",
                          background: "#fff",
                        }}
                      >
                        {/* Count */}
                        <div className="px-3 py-2 border-top border-bottom bg-light d-flex justify-content-between">
                          <strong>Count items</strong>
                          <span className="text-muted small">
                            {countItems.length}
                          </span>
                        </div>

                        {countItems.length === 0 ? (
                          <div className="px-3 py-3 text-muted small">
                            No count items in this set.
                          </div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {countItems.map((item) => (
                              <li key={item.id} className="list-group-item">
                                {renderSensor(item, setDocId)}
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Serial */}
                        <div className="px-3 py-2 border-bottom bg-light d-flex justify-content-between">
                          <strong>Serial items</strong>
                          <span className="text-muted small">
                            {serialItems.length}
                          </span>
                        </div>

                        {serialItems.length === 0 ? (
                          <div className="px-3 py-3 text-muted small">
                            No serial items in this set.
                          </div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {serialItems.map((item) => (
                              <li key={item.id} className="list-group-item">
                                {renderSensor(item, setDocId)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: REQUESTS + TRANSACTIONS */}
                  <div className="col-12 col-lg-6">
                    {/* Requests */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Open Requests</h6>
                        <small className="text-muted">
                          {setRequestsOpen.length} open
                        </small>
                      </div>

                      <div
                        className="border rounded"
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ maxHeight: "34vh", overflowY: "auto" }}>
                          {paginatedRequests.length === 0 ? (
                            <div className="px-3 py-3 text-muted small">
                              No open requests.
                            </div>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {paginatedRequests.map((r) => {
                                const req = Number(r.requestedQuantity || 0);
                                const ful = Number(r.fulfilledQuantity || 0);
                                const pct =
                                  r.itemType === "serial"
                                    ? r.status === "fulfilled"
                                      ? 100
                                      : 0
                                    : req > 0
                                      ? Math.min(
                                          100,
                                          Math.round((ful / req) * 100),
                                        )
                                      : 0;

                                return (
                                  <li key={r.id} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                                      <div
                                        style={{
                                          minWidth: 220,
                                          paddingRight: 10,
                                        }}
                                      >
                                        <div className="d-flex align-items-center flex-wrap">
                                          <strong className="mr-2">
                                            {r.name}
                                          </strong>
                                          <span className="badge badge-light mr-2">
                                            {r.itemType === "serial"
                                              ? "Serial"
                                              : "Count"}
                                          </span>
                                          <span className="text-muted small">
                                            {r.itemType === "serial"
                                              ? "Qty: 1"
                                              : `Qty: ${ful}/${req}`}
                                          </span>
                                        </div>

                                        <div className="mt-2">
                                          <div
                                            className="progress"
                                            style={{ height: 8 }}
                                          >
                                            <div
                                              className="progress-bar"
                                              role="progressbar"
                                              style={{ width: `${pct}%` }}
                                            />
                                          </div>
                                          <div className="d-flex justify-content-between mt-1">
                                            <small className="text-muted">
                                              {new Date(
                                                r.timestamp,
                                              ).toLocaleString("en-GB")}
                                            </small>
                                            <small className="text-muted">
                                              {r.createdBy || "Unknown"}
                                            </small>
                                          </div>
                                        </div>

                                        {r.note ? (
                                          <div className="small text-muted mt-1">
                                            Note: {r.note}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div
                                        className="d-flex flex-wrap justify-content-end mt-2 mt-md-0"
                                        style={{ gap: 6 }}
                                      >
                                        <button
                                          className="btn btn-sm btn-outline-secondary py-0 px-3"
                                          onClick={async () => {
                                            await updateDoc(
                                              doc(
                                                db,
                                                SENSOR_REQUESTS_COLLECTION,
                                                r.id,
                                              ),
                                              {
                                                fulfilledQuantity:
                                                  r.requestedQuantity,
                                                status: "fulfilled",
                                              },
                                            );
                                            await fetchData();
                                          }}
                                        >
                                          Fulfilled
                                        </button>

                                        <button
                                          className="btn btn-sm btn-outline-danger py-0 px-3"
                                          onClick={() => cancelRequest(r.id)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>

                        {setRequestsOpen.length > itemsPerPage && (
                          <div className="border-top p-2 d-flex justify-content-center">
                            <div className="d-flex align-items-center">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentReqPage <= 1}
                                onClick={() =>
                                  updateSetReqPage(
                                    setDocId,
                                    Math.max(1, currentReqPage - 1),
                                  )
                                }
                              >
                                Prev
                              </button>
                              <div className="px-2 small text-muted">
                                Page {currentReqPage} / {totalReqPages}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentReqPage >= totalReqPages}
                                onClick={() =>
                                  updateSetReqPage(
                                    setDocId,
                                    Math.min(totalReqPages, currentReqPage + 1),
                                  )
                                }
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transactions */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Recent Transactions</h6>
                        <small className="text-muted">
                          {setTransfers.length} total
                        </small>
                      </div>

                      <div
                        className="border rounded"
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ maxHeight: "34vh", overflowY: "auto" }}>
                          {paginatedTransfers.length === 0 ? (
                            <div className="px-3 py-3 text-muted small">
                              No transactions yet.
                            </div>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {paginatedTransfers.map((t) => (
                                <li key={t.id} className="list-group-item">
                                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                                    <div
                                      style={{
                                        minWidth: 220,
                                        paddingRight: 10,
                                      }}
                                    >
                                      <div className="mb-1">
                                        <strong>{t.name}</strong>
                                      </div>
                                      <div className="small">
                                        <span className="text-muted">
                                          From:
                                        </span>{" "}
                                        <strong>
                                          {t.fromSetId || t.fromSetDocId}
                                        </strong>{" "}
                                        <span className="text-muted">
                                          → To:
                                        </span>{" "}
                                        <strong>
                                          {t.toSetId || t.toSetDocId}
                                        </strong>
                                      </div>
                                      <div className="small text-muted mt-1">
                                        Qty: <strong>{t.quantity}</strong>
                                        {t.note ? <> · Note: {t.note}</> : null}
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {new Date(t.timestamp).toLocaleString(
                                          "en-GB",
                                        )}{" "}
                                        — {t.createdBy || "Unknown"}
                                      </div>
                                    </div>

                                    <div className="mt-2 mt-md-0">
                                      <button
                                        className="btn btn-sm btn-outline-danger py-0 px-3"
                                        onClick={async () => {
                                          const confirmDelete = window.confirm(
                                            "Delete this transaction?",
                                          );
                                          if (!confirmDelete) return;
                                          await deleteDoc(
                                            doc(
                                              db,
                                              SENSOR_TRANSFERS_COLLECTION,
                                              t.id,
                                            ),
                                          );
                                          await fetchData();
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {setTransfers.length > itemsPerPage && (
                          <div className="border-top p-2 d-flex justify-content-center">
                            <div className="d-flex align-items-center">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentTxPage <= 1}
                                onClick={() =>
                                  updateSetTxPage(
                                    setDocId,
                                    Math.max(1, currentTxPage - 1),
                                  )
                                }
                              >
                                Prev
                              </button>
                              <div className="px-2 small text-muted">
                                Page {currentTxPage} / {totalTxPages}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentTxPage >= totalTxPages}
                                onClick={() =>
                                  updateSetTxPage(
                                    setDocId,
                                    Math.min(totalTxPages, currentTxPage + 1),
                                  )
                                }
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======================================================
                  MODALS
                  ====================================================== */}

              {/* Transfer Modal */}
              {showTransferModal && (
                <div className="modal d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Transfer Sensor to Sleep Set
                        </h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: "1.25rem",
                            lineHeight: "1",
                            padding: "0.25rem 0.4rem",
                          }}
                          onClick={() => setShowTransferModal(false)}
                        >
                          X
                        </button>
                      </div>

                      <div className="modal-body">
                        <p>
                          <strong>{transferData.name}</strong>
                          <br />
                          {transferData.itemType === "serial"
                            ? `Serial Number: ${transferData.serialNumber || "-"}`
                            : `Current Quantity: ${transferData.currentQuantity}`}
                        </p>

                        <label>Transfer to Sleep Set:</label>
                        <select
                          className="form-control mb-2"
                          value={transferData.toSetDocId}
                          onChange={(e) =>
                            setTransferData({
                              ...transferData,
                              toSetDocId: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select Sleep Set --</option>
                          {sortedSets
                            .filter((s) => s.id !== transferData.fromSetDocId)
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.setId || s.id}
                              </option>
                            ))}
                        </select>

                        {transferData.itemType === "count" && (
                          <>
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              className="form-control mb-2"
                              value={transferData.quantity}
                              onChange={(e) =>
                                setTransferData({
                                  ...transferData,
                                  quantity:
                                    e.target.value === ""
                                      ? ""
                                      : parseInt(e.target.value, 10),
                                })
                              }
                            />
                          </>
                        )}

                        <label>Note:</label>
                        <input
                          className="form-control mb-2"
                          value={transferData.note || ""}
                          onChange={(e) =>
                            setTransferData({
                              ...transferData,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowTransferModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleTransfer}
                        >
                          Confirm Transfer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Sensor Modal */}
              {showCreateModal && (
                <div className="modal d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Add New Sensor</h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: "1.25rem",
                            lineHeight: "1",
                            padding: "0.25rem 0.4rem",
                          }}
                          onClick={() => setShowCreateModal(false)}
                        >
                          X
                        </button>
                      </div>

                      <div className="modal-body">
                        <label>Sleep Set:</label>
                        <select
                          className="form-control mb-2"
                          value={newSensor.setDocId}
                          onChange={(e) =>
                            setNewSensor({
                              ...newSensor,
                              setDocId: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select Sleep Set --</option>
                          {sortedSets.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.setId || s.id}
                            </option>
                          ))}
                        </select>

                        <label>Name:</label>
                        <input
                          className="form-control mb-2"
                          value={newSensor.name}
                          onChange={(e) =>
                            setNewSensor({ ...newSensor, name: e.target.value })
                          }
                        />

                        <label>Type:</label>
                        <select
                          className="form-control mb-2"
                          value={newSensor.type}
                          onChange={(e) =>
                            setNewSensor({ ...newSensor, type: e.target.value })
                          }
                        >
                          <option value="count">Count</option>
                          <option value="serial">Serial</option>
                        </select>

                        {newSensor.type === "serial" ? (
                          <>
                            <label>Serial Number:</label>
                            <input
                              className="form-control mb-2"
                              value={newSensor.serialNumber}
                              onChange={(e) =>
                                setNewSensor({
                                  ...newSensor,
                                  serialNumber: e.target.value,
                                })
                              }
                            />
                          </>
                        ) : (
                          <>
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              className="form-control mb-2"
                              value={newSensor.quantity}
                              onChange={(e) =>
                                setNewSensor({
                                  ...newSensor,
                                  quantity:
                                    e.target.value === ""
                                      ? ""
                                      : parseInt(e.target.value, 10),
                                })
                              }
                            />
                          </>
                        )}

                        <label>Note:</label>
                        <input
                          className="form-control mb-2"
                          value={newSensor.note || ""}
                          onChange={(e) =>
                            setNewSensor({ ...newSensor, note: e.target.value })
                          }
                        />
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowCreateModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleCreateSensor}
                        >
                          Create
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Count Modal */}
              {showAddCountModal && (
                <div className="modal d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Add Quantity</h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: "1.25rem",
                            lineHeight: "1",
                            padding: "0.25rem 0.4rem",
                          }}
                          onClick={() => setShowAddCountModal(false)}
                        >
                          X
                        </button>
                      </div>

                      <div className="modal-body">
                        <p>
                          <strong>{addCountData.name}</strong>
                          <br />
                          Current Quantity: {addCountData.currentQuantity}
                        </p>

                        <label>Quantity to Add:</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={addCountData.quantityToAdd}
                          onChange={(e) =>
                            setAddCountData({
                              ...addCountData,
                              quantityToAdd:
                                e.target.value === ""
                                  ? ""
                                  : parseInt(e.target.value, 10),
                            })
                          }
                        />
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowAddCountModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            const {
                              setDocId,
                              sensorId,
                              currentQuantity,
                              quantityToAdd,
                              name,
                            } = addCountData;

                            const qty = Number(quantityToAdd) || 0;
                            if (!setDocId || !sensorId || qty <= 0) return;

                            const ref = doc(
                              db,
                              `${SLEEPSETS_COLLECTION}/${setDocId}/${SENSOR_SUBCOLLECTION}/${sensorId}`,
                            );

                            await updateDoc(ref, {
                              quantity: Number(currentQuantity || 0) + qty,
                            });

                            await addDoc(
                              collection(db, SENSOR_TRANSFERS_COLLECTION),
                              {
                                type: "MANUAL_ADD",
                                sensorId,
                                name,
                                itemType: "count",
                                quantity: qty,
                                fromSetId: "Center",
                                toSetDocId: setDocId,
                                timestamp: new Date().toISOString(),
                                createdBy: currentUser.displayName || "",
                              },
                            );

                            await autoFulfillRequests({
                              setDocId,
                              name,
                              itemType: "count",
                              addedQuantity: qty,
                            });

                            setShowAddCountModal(false);
                            await fetchData();
                          }}
                        >
                          Confirm Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Scrap/Consume Modal */}
              {showReleaseConsumeModal && (
                <div className="modal d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {releaseConsumeData.type === "scrap"
                            ? "Scrap"
                            : "Consume"}{" "}
                          Sensor
                        </h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: "1.25rem",
                            lineHeight: "1",
                            padding: "0.25rem 0.4rem",
                          }}
                          onClick={() => setShowReleaseConsumeModal(false)}
                        >
                          X
                        </button>
                      </div>

                      <div className="modal-body">
                        <p>
                          <strong>{releaseConsumeData.name}</strong>
                          <br />
                          {releaseConsumeData.itemType === "serial"
                            ? `Serial Number: ${releaseConsumeData.serialNumber || "-"}`
                            : `Current Quantity: ${releaseConsumeData.currentQuantity}`}
                        </p>

                        <label>Action Type:</label>
                        <select
                          className="form-control mb-2"
                          value={releaseConsumeData.type}
                          onChange={(e) =>
                            setReleaseConsumeData({
                              ...releaseConsumeData,
                              type: e.target.value,
                            })
                          }
                        >
                          <option value="consume">Consume</option>
                          <option value="scrap">Scrap</option>
                        </select>

                        {releaseConsumeData.itemType === "count" && (
                          <>
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              max={releaseConsumeData.currentQuantity}
                              className="form-control mb-2"
                              value={releaseConsumeData.quantity}
                              onChange={(e) =>
                                setReleaseConsumeData({
                                  ...releaseConsumeData,
                                  quantity:
                                    e.target.value === ""
                                      ? ""
                                      : parseInt(e.target.value, 10),
                                })
                              }
                            />
                          </>
                        )}

                        <label>Note:</label>
                        <input
                          className="form-control mb-2"
                          value={releaseConsumeData.note || ""}
                          onChange={(e) =>
                            setReleaseConsumeData({
                              ...releaseConsumeData,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowReleaseConsumeModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            const {
                              setDocId,
                              sensorId,
                              quantity,
                              currentQuantity,
                              name,
                              type,
                              itemType,
                            } = releaseConsumeData;

                            const ref = doc(
                              db,
                              `${SLEEPSETS_COLLECTION}/${setDocId}/${SENSOR_SUBCOLLECTION}/${sensorId}`,
                            );

                            if (itemType === "serial") {
                              await deleteDoc(ref);
                            } else {
                              await updateDoc(ref, {
                                quantity: Math.max(
                                  0,
                                  Number(currentQuantity || 0) -
                                    Number(quantity || 0),
                                ),
                              });
                            }

                            await addDoc(
                              collection(db, SENSOR_TRANSFERS_COLLECTION),
                              {
                                type: "SCRAP_CONSUME",
                                sensorId,
                                name,
                                itemType,
                                quantity:
                                  itemType === "serial"
                                    ? 1
                                    : Number(quantity || 0),
                                fromSetDocId: setDocId,
                                to: type === "scrap" ? "Scrapped" : "Consumed",
                                note: releaseConsumeData.note || type,
                                timestamp: new Date().toISOString(),
                                createdBy: currentUser.displayName || "",
                              },
                            );

                            setShowReleaseConsumeModal(false);
                            await fetchData();
                          }}
                        >
                          Confirm{" "}
                          {releaseConsumeData.type === "scrap"
                            ? "Scrap"
                            : "Consume"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Modal */}
              {showRequestModal && (
                <div className="modal d-block" tabIndex="-1" role="dialog">
                  <div className="modal-dialog" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Request Sensor</h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: "1.25rem",
                            lineHeight: "1",
                            padding: "0.25rem 0.4rem",
                          }}
                          onClick={() => setShowRequestModal(false)}
                        >
                          X
                        </button>
                      </div>

                      <div className="modal-body">
                        <label>Sleep Set:</label>
                        <select
                          className="form-control mb-2"
                          value={requestData.setDocId}
                          onChange={(e) =>
                            setRequestData({
                              ...requestData,
                              setDocId: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select Sleep Set --</option>
                          {sortedSets.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.setId || s.id}
                            </option>
                          ))}
                        </select>

                        <label>Name:</label>
                        <input
                          className="form-control mb-2"
                          value={requestData.name}
                          onChange={(e) =>
                            setRequestData({
                              ...requestData,
                              name: e.target.value,
                            })
                          }
                        />

                        <label>Type:</label>
                        <select
                          className="form-control mb-2"
                          value={requestData.itemType}
                          onChange={(e) =>
                            setRequestData({
                              ...requestData,
                              itemType: e.target.value,
                            })
                          }
                        >
                          <option value="count">Count</option>
                          <option value="serial">Serial</option>
                        </select>

                        {requestData.itemType === "count" && (
                          <>
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              className="form-control mb-2"
                              value={requestData.quantity}
                              onChange={(e) =>
                                setRequestData({
                                  ...requestData,
                                  quantity:
                                    e.target.value === ""
                                      ? ""
                                      : parseInt(e.target.value, 10),
                                })
                              }
                            />
                          </>
                        )}

                        <label>Note:</label>
                        <input
                          className="form-control mb-2"
                          value={requestData.note || ""}
                          onChange={(e) =>
                            setRequestData({
                              ...requestData,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowRequestModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleCreateRequest}
                        >
                          Create Request
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tx pagination (per set) */}
              {setTransfers.length > itemsPerPage && (
                <div className="border-top p-2 d-flex justify-content-center">
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={currentTxPage <= 1}
                      onClick={() =>
                        updateSetTxPage(
                          setDocId,
                          Math.max(1, currentTxPage - 1),
                        )
                      }
                    >
                      Prev Tx
                    </button>
                    <div className="px-2 small text-muted">
                      Page {currentTxPage} / {totalTxPages}
                    </div>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={currentTxPage >= totalTxPages}
                      onClick={() =>
                        updateSetTxPage(
                          setDocId,
                          Math.min(totalTxPages, currentTxPage + 1),
                        )
                      }
                    >
                      Next Tx
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* NOTE: you can keep other paginations as-is */}
    </div>
  );
};

export default SensorStockPage;
